import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from "../contexts/ThemeContext";

/**
 * Converts theme object to CSS custom properties
 * This allows you to use theme values in your CSS files
 *
 * @param {Object} theme - Theme object
 * @returns {string} CSS custom properties string
 */
export const themeToCSS = (theme) => {
  const cssVars = [];

  // Colors
  Object.entries(colors).forEach(([colorName, colorValues]) => {
    if (typeof colorValues === "object" && colorValues !== null) {
      Object.entries(colorValues).forEach(([shade, value]) => {
        cssVars.push(`--color-${colorName}-${shade}: ${value};`);
      });
    } else {
      cssVars.push(`--color-${colorName}: ${colorValues};`);
    }
  });

  // Typography
  Object.entries(typography.fonts).forEach(([name, value]) => {
    cssVars.push(`--font-${name}: ${value};`);
  });

  Object.entries(typography.fontSizes).forEach(([size, value]) => {
    cssVars.push(`--font-size-${size}: ${value};`);
  });

  Object.entries(typography.fontWeights).forEach(([weight, value]) => {
    cssVars.push(`--font-weight-${weight}: ${value};`);
  });

  Object.entries(typography.lineHeights).forEach(([height, value]) => {
    cssVars.push(`--line-height-${height}: ${value};`);
  });

  // Spacing
  Object.entries(spacing).forEach(([space, value]) => {
    cssVars.push(`--spacing-${space}: ${value};`);
  });

  // Border Radius
  Object.entries(borderRadius).forEach(([radius, value]) => {
    cssVars.push(`--border-radius-${radius}: ${value};`);
  });

  // Shadows
  Object.entries(shadows).forEach(([shadow, value]) => {
    cssVars.push(`--shadow-${shadow}: ${value};`);
  });

  return `:root {\n  ${cssVars.join("\n  ")}\n}`;
};

/**
 * Generates CSS custom properties and injects them into the document
 * Call this function in your main App component or index.js
 */
export const injectThemeCSS = () => {
  const cssString = themeToCSS();

  // Check if theme styles already exist
  let existingStyle = document.getElementById("theme-css-vars");

  if (existingStyle) {
    existingStyle.textContent = cssString;
  } else {
    const style = document.createElement("style");
    style.id = "theme-css-vars";
    style.textContent = cssString;
    document.head.appendChild(style);
  }
};

/**
 * Helper function to get CSS custom property value
 * @param {string} property - CSS custom property name (without --)
 * @returns {string} CSS custom property value
 *
 * @example
 * const primaryColor = getCSSVar('color-primary-500');
 * const fontSize = getCSSVar('font-size-lg');
 */
export const getCSSVar = (property) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(`--${property}`)
    .trim();
};

export default themeToCSS;
